(function () {
  "use strict";

  // localStorage (not sessionStorage) so the chat survives following a product
  // link the bot opens in a NEW TAB: sessionStorage is per-tab, so the new tab
  // would start a fresh chat; localStorage is shared, so it restores the same
  // session. The 30-min server-side session TTL still bounds its lifetime.
  var SID_KEY = "bici_chat_session_id";
  var AUTH_KEY = "bici_chat_logged_in";
  // The anonymous session a visitor was chatting in when they logged in. Held
  // until the next POST /session so the backend can move that conversation
  // onto their account instead of starting over.
  var PRELOGIN_KEY = "bici_chat_prelogin_session_id";
  // Rich result widgets (product cards, order buttons, cart banner, login
  // button) are NOT part of the server-side text history, so any rebuild of the
  // transcript from the server (reopen, page navigation) would drop them.
  // Persist them here, keyed to the session and anchored to the message they
  // followed, and re-render them in renderHistory(). Tied to SID_KEY's
  // lifecycle: cleared whenever the session is dropped or replaced.
  var ENRICH_KEY = "bici_chat_enrichments";
  // Epoch ts of the server's latest "welcome back" recap (resumed_at on
  // GET /session). When it changes, the server has hidden the pre-gap
  // transcript behind a recap line — enrichments saved against those hidden
  // messages would anchor to the wrong rows, so they're dropped.
  var RESUME_KEY = "bici_chat_resume_ts";
  // Whether the panel was left open, so it re-opens on the next page — the
  // session + enrichments above restore the transcript and cards. This is what
  // keeps the panel open when a shopper follows a link on desktop.
  var OPEN_KEY = "bici_chat_open";

  var config = window.BiciChatbotConfig || {};
  var apiUrl = (config.apiUrl || "").replace(/\/+$/, "");
  if (!apiUrl) return;

  // Storefront-minted HMAC token identifying the logged-in customer (empty
  // string when anonymous or the secret isn't configured).
  var customerToken = config.customerToken || null;
  var customerLoggedIn = !!config.customerLoggedIn;

  // A logged-in shopper with no token means the backend will see them as
  // anonymous (no order lookup, etc). The usual cause is an unset "Customer
  // token secret" theme setting — surface it so it's diagnosable rather than
  // silently broken. See snippets/bici-chatbot.liquid + app/auth.py.
  if (customerLoggedIn && !customerToken) {
    console.warn("[BiciChatbot] Customer is logged in but no customer token was minted — set the 'Customer token secret' theme setting (must match the backend CUSTOMER_TOKEN_SECRET).");
  }

  // If the login state changed since the stored session was created (login OR
  // logout), drop the stored session. Logging IN keeps the anonymous chat's id
  // aside so the backend can carry that conversation onto the account; logging
  // OUT drops everything, so on a shared computer the next visitor can't
  // restore the previous customer's chat history.
  try {
    var prevAuth = localStorage.getItem(AUTH_KEY);
    if (prevAuth !== null && prevAuth !== String(customerLoggedIn)) {
      var anonSid = localStorage.getItem(SID_KEY);
      localStorage.removeItem(SID_KEY);
      localStorage.removeItem(PRELOGIN_KEY);
      if (customerLoggedIn && anonSid) {
        // Logging IN: carry the anonymous chat over. Its saved product cards
        // stay too (keyed to the session id, so they only show if the backend
        // hands back this same session).
        localStorage.setItem(PRELOGIN_KEY, anonSid);
      } else {
        // Logging OUT: drop everything so the next person on a shared
        // computer can't restore the previous customer's chat.
        localStorage.removeItem(ENRICH_KEY);
      }
    }
    localStorage.setItem(AUTH_KEY, String(customerLoggedIn));
  } catch (e) {}

  // One-time cleanup of the retired home-page-only visibility flags. The widget
  // used to be hidden everywhere except the home page (and pages reached by
  // following a bot-rendered link), carried across by these two keys. It now
  // shows site-wide, so purge any stale entries left in returning visitors'
  // localStorage.
  try {
    localStorage.removeItem("bici_chat_follow");
    localStorage.removeItem("bici_chat_visible_path");
  } catch (e) {}

  var root = document.getElementById("BiciChatbot");
  if (!root) return;

  var launcher = root.querySelector(".bici-chatbot__launcher");
  var panel = root.querySelector(".bici-chatbot__panel");
  var headerEl = root.querySelector(".bici-chatbot__header");
  var closeBtn = root.querySelector(".bici-chatbot__close");
  var statusEl = root.querySelector(".bici-chatbot__status");
  var messagesEl = root.querySelector(".bici-chatbot__messages");
  var formEl = root.querySelector(".bici-chatbot__form");
  var inputEl = root.querySelector(".bici-chatbot__input");
  var sendBtn = root.querySelector(".bici-chatbot__send");
  var chipsEl = root.querySelector(".bici-chatbot__chips");
  var chipsWrapEl = root.querySelector(".bici-chatbot__chips-wrap");
  var chipArrowL = root.querySelector(".bici-chatbot__chips-arrow--left");
  var chipArrowR = root.querySelector(".bici-chatbot__chips-arrow--right");
  var chipsToggle = root.querySelector(".bici-chatbot__chips-toggle");

  var sessionId = null;
  var initialized = false;
  var initializing = null;
  var greeting = (config.greeting || "").trim();

  // Live agent takeover: when a human support agent takes the chat over from
  // the dashboard, the backend stops calling the LLM. The widget then polls
  // GET /session for the agent's replies and surfaces a "you're talking to a
  // person" banner. See app/api.py (agent_takeover) on the backend.
  var agentTakeover = false;
  var pollTimer = null;
  // Pause the background poll's re-render while a /chat turn is mid-flight, so
  // it can't wipe the optimistic message / "Thinking…" bubble.
  var turnInFlight = false;
  // Signature of the last server state we rendered, so the poll only rebuilds
  // the transcript when something actually changed.
  var lastSig = "";
  // How many server-history messages are reflected in the DOM, so the poll can
  // APPEND only new ones instead of clearing and rebuilding — a rebuild renders
  // from text-only server history and would wipe locally-rendered product cards,
  // order buttons and cart banners (the "results disappear on scroll" bug).
  var renderedLen = 0;
  // Set when the chat carried over at login was waiting on that login (the
  // bot had just asked them to log in to see their orders). The next open
  // sends a logged_in event so the bot answers without being asked again.
  var pendingLoginContinue = false;

  var ORDER_BASE_URL = "https://account.bici.cc/orders/";
  var ALL_ORDERS_URL = "https://account.bici.cc";
  var DEFAULT_INPUT_PLACEHOLDER = (inputEl && inputEl.getAttribute("placeholder")) || "Ask a question…";
  // Inline placeholder for imageless product cards — no third-party request.
  var NO_IMAGE_SRC = "data:image/svg+xml," + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="220" height="140">' +
    '<rect width="100%" height="100%" fill="#f0f0f0"/>' +
    '<text x="50%" y="50%" fill="#999" font-family="sans-serif" font-size="13" ' +
    'text-anchor="middle" dominant-baseline="middle">No image</text></svg>');

  // Streaming replies (POST /chat/stream, SSE): text renders as the model
  // writes instead of waiting behind "Thinking…". Feature-detected, and
  // streamEndpointOk flips off (for this page) if the backend 404s so we fall
  // back to the buffered /chat path.
  var canStream = typeof window.ReadableStream !== "undefined" &&
                  typeof window.TextDecoder !== "undefined";
  var streamEndpointOk = true;
  // Shopper-facing labels for the typing bubble while a tool runs.
  var TOOL_STATUS_LABELS = {
    products_search: "Searching products…",
    product_get: "Getting product details…",
    policies_faqs_search: "Checking store policies…",
    cart_update: "Updating your cart…",
    cart_get: "Checking your cart…",
    orders_lookup: "Looking up your orders…",
    show_order_buttons: "Fetching order links…",
    shipping_timelines: "Checking delivery times…",
    create_warranty_ticket: "Creating your warranty ticket…",
    create_sales_lead: "Sending your request…",
    report_site_issue: "Notifying our team…",
    create_support_ticket: "Creating your support ticket…",
    create_service_booking: "Sending your booking request…"
  };

  // Customer-linked sessions require proof of identity to read back (the
  // backend's GET /session/{id} checks this header); anonymous sessions
  // ignore it.
  function sessionGetOpts() {
    return customerToken ? { headers: { "X-Customer-Token": customerToken } } : {};
  }

  // Backend-supplied URLs (product links, checkout, images) must be http(s):
  // a javascript: or data: URL in a tampered response must never become a
  // clickable href. Returns the resolved URL or null.
  function safeUrl(u) {
    if (!u) return null;
    try {
      var parsed = new URL(u, window.location.origin);
      if (parsed.protocol === "http:" || parsed.protocol === "https:") return parsed.href;
    } catch (e) {}
    return null;
  }

  function el(tag, className, text) {
    var n = document.createElement(tag);
    if (className) n.className = className;
    if (text != null) n.textContent = text;
    return n;
  }

  // Every scroll the widget performs itself goes through here, so the listener
  // further down can tell them apart from the reader moving the list by hand —
  // the browser fires the same event for both.
  var progScrollUntil = 0;
  function setScrollTop(top) {
    progScrollUntil = Date.now() + 150;
    messagesEl.scrollTop = top;
  }

  function scrollToBottom() {
    setScrollTop(messagesEl.scrollHeight);
  }

  // While true, content-adding helpers skip their normal jump-to-bottom. Used to
  // batch a whole incoming turn (reply text + any cards/orders/cart) so the
  // scroll position is decided once, after the turn has rendered.
  var holdScroll = false;
  function autoScroll() {
    if (!holdScroll) scrollToBottom();
  }

  // Follow new content toward the bottom, but never past the point where the
  // visitor's last message has just scrolled out of view. A short reply lands
  // at the bottom as usual; a long one stops with the viewport at the start of
  // the unread content, visibly cut off at the fold so it's apparent there is
  // more to read below. If the visitor's message is itself the last thing in
  // the transcript, the cap sits past the bottom and this is a plain
  // scroll-to-bottom.
  function scrollForNewContent() {
    var target = messagesEl.scrollHeight - messagesEl.clientHeight;
    var users = messagesEl.querySelectorAll(".bici-chatbot__msg--user");
    if (users.length) {
      var hideAt = users[users.length - 1].getBoundingClientRect().bottom -
                   messagesEl.getBoundingClientRect().top + messagesEl.scrollTop;
      target = Math.min(target, hideAt);
    }
    // Forward only: if the reader has already scrolled past the cap on their
    // own, don't drag them back up.
    if (target > messagesEl.scrollTop) setScrollTop(target);
    keepCarouselBarVisible();
  }

  // ── Trailing product carousel ─────────────────────────────────────────────
  // When the newest thing in the transcript is a product carousel, its
  // horizontal scroll bar is the only cue that more products sit off to the
  // right. Keep the transcript parked low enough that the bar stays on screen
  // while the panel's other rows take height away from it — the composer
  // growing with a long message, the quick-action chips sliding back in, the
  // mobile keyboard shrinking the panel — unless the reader has scrolled up,
  // in which case where they're reading wins.
  var CAROUSEL_BAR_GAP = 6;   // clearance below the bar so it isn't flush to the edge
  var followCarousel = true;  // cleared while the reader sits above the floor

  // The card row of a product carousel that is both the last thing rendered
  // and still expanded; null in every other case.
  function trailingCards() {
    var last = messagesEl.lastElementChild;
    if (!last || !last.classList.contains("bici-chatbot__results")) return null;
    if (last.classList.contains("is-collapsed")) return null;
    return last.querySelector(".bici-chatbot__cards");
  }

  // Lowest scroll position that still leaves that row's scroll bar in view,
  // clamped to the list's own range. null when there's no trailing carousel.
  function carouselFloor() {
    var cards = trailingCards();
    if (!cards) return null;
    var below = cards.getBoundingClientRect().bottom + CAROUSEL_BAR_GAP -
                messagesEl.getBoundingClientRect().bottom;
    var floor = messagesEl.scrollTop + below;
    return Math.max(0, Math.min(floor, messagesEl.scrollHeight - messagesEl.clientHeight));
  }

  function keepCarouselBarVisible() {
    if (!followCarousel) return;
    var floor = carouselFloor();
    if (floor != null && messagesEl.scrollTop < floor - 1) setScrollTop(floor);
  }

  // A scroll this widget didn't cause is the reader taking over: coming to rest
  // above the floor means they've scrolled up to read something, so stop
  // pulling them back down until they come back to it.
  messagesEl.addEventListener("scroll", function () {
    if (Date.now() < progScrollUntil) return;
    var floor = carouselFloor();
    followCarousel = floor == null || messagesEl.scrollTop >= floor - 2;
  }, { passive: true });

  // The panel is one flex column, so anything that makes another row taller
  // shrinks the transcript's viewport and can push the bar out of sight.
  // Watching the list's own box catches every cause at once — typing, chips,
  // keyboard, sheet drag — including the frames of an in-flight transition.
  if (window.ResizeObserver) {
    new ResizeObserver(function () { keepCarouselBarVisible(); }).observe(messagesEl);
  }
  // Fallback for browsers without ResizeObserver: the chips row animates its
  // height, so re-check once it has settled.
  else if (chipsEl) {
    chipsEl.addEventListener("transitionend", function (e) {
      if (e.propertyName === "max-height") keepCarouselBarVisible();
    });
  }

  function addMessage(role, text, isAgent) {
    // A reply typed by a human support agent during takeover: tag it so the
    // visitor knows they're no longer talking to the bot.
    if (role === "assistant" && isAgent) {
      messagesEl.appendChild(el("div", "bici-chatbot__agent-tag", "Support agent"));
    }
    var cls = "bici-chatbot__msg bici-chatbot__msg--" + (role === "user" ? "user" : "assistant");
    if (role === "assistant" && isAgent) cls += " bici-chatbot__msg--agent";
    var node = el("div", cls, text);
    messagesEl.appendChild(node);
    autoScroll();
    return node;
  }

  // ── Persisted rich widgets (products / orders / cart / login) ─────────────
  // Each entry is { after, products, orders, cart, shipping, login_url }, where
  // `after` is the server history length at the moment the turn produced it —
  // i.e. the widgets render immediately after the `after`-th message. Stored
  // per session so a different or expired session never shows another
  // conversation's results.
  function loadEnrichments() {
    try {
      var data = JSON.parse(localStorage.getItem(ENRICH_KEY) || "null");
      if (!data || data.sid !== sessionId || !Array.isArray(data.items)) return [];
      return data.items;
    } catch (e) { return []; }
  }

  function saveEnrichment(after, payload) {
    try {
      var items = loadEnrichments();
      items.push({
        after: after,
        products: payload.products || [],
        orders: payload.orders || [],
        cart: payload.cart || null,
        shipping: payload.shipping || null,
        login_url: payload.login_url || null
      });
      // Bound growth on long-lived sessions; keep the most recent.
      if (items.length > 60) items = items.slice(items.length - 60);
      localStorage.setItem(ENRICH_KEY, JSON.stringify({ sid: sessionId, items: items }));
    } catch (e) {
      // Quota/serialization failure: drop the persisted copy rather than throw.
      // The widgets still show for this turn; they just won't survive a reload.
      try { localStorage.removeItem(ENRICH_KEY); } catch (e2) {}
    }
  }

  function clearEnrichments() {
    try { localStorage.removeItem(ENRICH_KEY); } catch (e) {}
  }

  function renderEnrichmentsAt(byAfter, n) {
    var items = byAfter[n];
    if (!items) return;
    items.forEach(function (it) {
      if (it.products && it.products.length) renderProductCards(it.products);
      if (it.orders && it.orders.length) renderOrderButtons(it.orders);
      if (it.login_url) renderLoginButton(it.login_url);
      if (it.cart) renderCartBanner(it.cart);
      if (it.shipping) renderShippingTable(it.shipping);
    });
  }

  function renderHistory(history) {
    history = history || [];
    // Re-render persisted rich widgets inline with the text transcript, grouped
    // by the message index they follow, so they survive a rebuild from history.
    var byAfter = {};
    loadEnrichments().forEach(function (it) {
      (byAfter[it.after] = byAfter[it.after] || []).push(it);
    });
    history.forEach(function (m, i) {
      addMessage(m.role, m.content, m.agent);
      renderEnrichmentsAt(byAfter, i + 1);
    });
  }

  function addError(text) {
    messagesEl.appendChild(el("div", "bici-chatbot__msg bici-chatbot__msg--error", text));
    scrollToBottom();
  }

  function renderProductCards(products) {
    if (!products || !products.length) return;
    var wrap = el("div", "bici-chatbot__results");

    // A header row with a deliberate Hide/Show control, so the shopper can
    // collapse results on demand instead of them vanishing on their own.
    var bar = el("div", "bici-chatbot__results-bar");
    bar.appendChild(el("span", "bici-chatbot__results-label",
      products.length === 1 ? "1 result" : products.length + " results"));
    var toggle = el("button", "bici-chatbot__results-toggle", "Hide results");
    toggle.type = "button";
    toggle.addEventListener("click", function () {
      var collapsed = wrap.classList.toggle("is-collapsed");
      toggle.textContent = collapsed ? "Show results" : "Hide results";
      // Re-expanding is a request to see the results: park the transcript so
      // the row's scroll bar is in view again.
      if (!collapsed) {
        followCarousel = true;
        keepCarouselBarVisible();
      }
    });
    bar.appendChild(toggle);
    wrap.appendChild(bar);

    var grid = el("div", "bici-chatbot__cards");
    products.forEach(function (p) {
      var card = el("div", "bici-chatbot__card");
      if (p.best_match) {
        card.appendChild(el("div", "bici-chatbot__badge", "Best match"));
      }
      var img = document.createElement("img");
      img.src = safeUrl(p.image_url) || NO_IMAGE_SRC;
      img.alt = p.title || "";
      card.appendChild(img);

      var body = el("div", "bici-chatbot__card-body");
      body.appendChild(el("div", "bici-chatbot__card-title", p.title || ""));
      body.appendChild(el("div", "bici-chatbot__card-price", p.price || ""));

      var actions = el("div", "bici-chatbot__card-actions");
      var productUrl = safeUrl(p.product_url);
      if (productUrl) {
        var a = document.createElement("a");
        a.href = productUrl;
        // Same-tab navigation to an on-site product page. On desktop the panel
        // stays open via OPEN_KEY auto-reopen (see openPanel/closePanel), so
        // the transcript is intact on arrival; on mobile the click handler
        // closes the fullscreen panel so the shopper sees the page. No
        // target="_blank".
        a.textContent = "View";
        actions.appendChild(a);
      }
      body.appendChild(actions);
      card.appendChild(body);
      grid.appendChild(card);
    });
    wrap.appendChild(grid);
    messagesEl.appendChild(wrap);
    // A carousel that just arrived is the newest content, so follow it again
    // even if the reader had scrolled up during the previous turn.
    followCarousel = true;
    autoScroll();
    if (!holdScroll) keepCarouselBarVisible();
  }

  // Turn a Shopify fulfillment status ("FULFILLED", "PARTIALLY_FULFILLED", "")
  // into something a shopper reads naturally.
  function humanizeStatus(s) {
    if (!s) return "";
    return String(s)
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/^\w/, function (c) { return c.toUpperCase(); });
  }

  // A vertical stack of order cards (number, date, status), each linking to the
  // order, followed by a "View all orders" link. Older persisted enrichments
  // carry only {id,name}, so date/status render only when present.
  function renderOrderButtons(orders) {
    if (!orders || !orders.length) return;
    var wrap = el("div", "bici-chatbot__orders");
    var any = false;
    orders.forEach(function (o) {
      if (o == null || o.id == null) return;
      any = true;
      var a = document.createElement("a");
      a.className = "bici-chatbot__order-card";
      a.href = ORDER_BASE_URL + encodeURIComponent(o.id);
      a.target = "_blank";
      a.rel = "noopener";
      a.appendChild(el("span", "bici-chatbot__order-name", o.name || ("Order " + o.id)));

      var metaBits = [];
      if (o.created_at) metaBits.push(o.created_at);
      var status = humanizeStatus(o.fulfillment_status);
      if (status) metaBits.push(status);
      if (metaBits.length) {
        a.appendChild(el("span", "bici-chatbot__order-meta", metaBits.join(" · ")));
      }
      wrap.appendChild(a);
    });
    if (!any) return;

    var all = document.createElement("a");
    all.className = "bici-chatbot__orders-all";
    all.href = ALL_ORDERS_URL;
    all.target = "_blank";
    all.rel = "noopener";
    all.textContent = "View all orders";
    wrap.appendChild(all);

    messagesEl.appendChild(wrap);
    autoScroll();
  }

  // "Log in" button under a reply that asked an anonymous shopper to sign in
  // for order support (j.login_url, set by the backend's orders_lookup).
  // Same-tab: the URL carries return_url, so they land back on this page.
  function renderLoginButton(url) {
    var href = safeUrl(url);
    if (!href) return;
    var wrap = el("div", "bici-chatbot__login");
    var a = el("a", "bici-chatbot__login-btn", "Log in");
    a.href = href;
    wrap.appendChild(a);
    messagesEl.appendChild(wrap);
    autoScroll();
  }

  // Compact delivery-times table from a shipping_timelines tool result.
  function renderShippingTable(shipping) {
    if (!shipping || !shipping.rows || !shipping.rows.length) return;
    var wrap = el("div", "bici-chatbot__shipping");

    // Header row with a deliberate Hide/Show control, same pattern as the
    // product results bar.
    var bar = el("div", "bici-chatbot__shipping-bar");
    bar.appendChild(el("div", "bici-chatbot__shipping-title", "Estimated delivery times"));
    var toggle = el("button", "bici-chatbot__shipping-toggle", "Hide table");
    toggle.type = "button";
    toggle.addEventListener("click", function () {
      var collapsed = wrap.classList.toggle("is-collapsed");
      toggle.textContent = collapsed ? "Show table" : "Hide table";
    });
    bar.appendChild(toggle);
    wrap.appendChild(bar);

    if (shipping.origin) {
      wrap.appendChild(el("div", "bici-chatbot__shipping-origin", "Ships from " + shipping.origin));
    }

    var hasExpress = shipping.rows.some(function (r) { return r.express; });

    var table = el("table", "bici-chatbot__shipping-table");
    var thead = document.createElement("thead");
    var htr = document.createElement("tr");
    htr.appendChild(el("th", null, "Province"));
    htr.appendChild(el("th", null, "Standard"));
    if (hasExpress) htr.appendChild(el("th", null, "Express"));
    thead.appendChild(htr);
    table.appendChild(thead);

    var tbody = document.createElement("tbody");
    shipping.rows.forEach(function (r) {
      var tr = document.createElement("tr");
      tr.appendChild(el("td", null, r.province_name || r.province || ""));
      tr.appendChild(el("td", null, r.standard || ""));
      if (hasExpress) tr.appendChild(el("td", null, r.express || "—"));
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    wrap.appendChild(table);

    messagesEl.appendChild(wrap);
    autoScroll();
  }

  function renderCartBanner(cart) {
    if (!cart || (!cart.checkout_url && !(cart.items || []).length)) return;
    var banner = el("div", "bici-chatbot__cart");

    var header = el("div", "bici-chatbot__cart-header");
    header.appendChild(el("span", "bici-chatbot__cart-title",
      "Cart (" + (cart.total_quantity || (cart.items || []).length) + " items)"));
    header.appendChild(el("span", "bici-chatbot__cart-total", cart.total || ""));
    banner.appendChild(header);

    if ((cart.items || []).length) {
      var lines = el("div", "bici-chatbot__cart-lines");
      cart.items.forEach(function (ln) {
        lines.appendChild(el("div", null,
          "• " + ln.quantity + "x " + ln.title + " — " + (ln.line_total || "")));
      });
      banner.appendChild(lines);
    }

    var checkoutUrl = safeUrl(cart.checkout_url);
    if (checkoutUrl) {
      var link = document.createElement("a");
      link.className = "bici-chatbot__cart-checkout";
      link.href = checkoutUrl;
      link.target = "_blank";
      link.rel = "noopener";
      link.textContent = "Go to checkout →";
      banner.appendChild(link);
    }

    messagesEl.appendChild(banner);
    autoScroll();
  }

  function clearMessages() {
    // A transcript rebuild would orphan an open report form (it lives inside
    // messagesEl) — reset its state so the chip can reopen a fresh one.
    closeReportBox();
    messagesEl.innerHTML = "";
  }

  function showGreetingIfEmpty() {
    if (greeting && !messagesEl.firstChild) {
      addMessage("assistant", greeting);
    }
  }

  // Quick-action chips (Track order / Returns / Warranty claim / Report an
  // issue / Warranty info) are fully shown in the empty/greeting state. Once
  // the conversation has messages they slide off to the right, so an active
  // chat stays uncluttered without losing access to the shortcuts: the
  // collapse toggle pinned at the right edge slides them off and back in
  // either way, in any state.
  function setChipsVisible(show) {
    var box = chipsWrapEl || chipsEl;
    if (!box) return;
    if (!chipsToggle) {
      // Cached copy of the snippet predates the collapse toggle (or the
      // wrapper) — fall back to the old show/hide behavior.
      box.hidden = !show;
    } else {
      box.hidden = false;
      box.classList.toggle("bici-chatbot__chips-wrap--collapsed", !show);
      chipsToggle.setAttribute("aria-expanded", show ? "true" : "false");
      chipsToggle.setAttribute("aria-label", show ? "Hide quick actions" : "Show quick actions");
      // Collapsed: the restore chevron is the only control. Expanded:
      // updateChipArrows() below decides whether it must yield the left
      // edge to the scroll-back arrow.
      if (!show) chipsToggle.hidden = false;
    }
    if (show) {
      // Lead with the far-left buttons whenever the row slides back in.
      if (chipsEl) chipsEl.scrollLeft = 0;
      updateChipArrows();
    }
    // The row's strip opens or closes over ~0.44s (max-height plus its delay);
    // ResizeObserver follows it frame by frame, and this covers the end state
    // for browsers without one.
    setTimeout(keepCarouselBarVisible, 480);
  }

  function setTakeover(on) {
    agentTakeover = !!on;
    if (statusEl) {
      statusEl.textContent = agentTakeover
        ? "You're now chatting with a member of our team."
        : "";
      statusEl.hidden = !agentTakeover;
    }
    if (inputEl) {
      inputEl.placeholder = agentTakeover
        ? "Reply to our team…"
        : DEFAULT_INPUT_PLACEHOLDER;
    }
  }

  // A compact fingerprint of the server-side conversation state, so the poll
  // only rebuilds the transcript when the history or takeover flag changes.
  function sessionSig(j) {
    var h = (j && j.history) || [];
    var n = h.length;
    var last = n ? (h[n - 1].role + "|" + (h[n - 1].agent ? "a" : "") + "|" + (h[n - 1].content || "")) : "";
    return (j && j.agent_takeover ? "T" : "F") + ":" + n + ":" + last;
  }

  // Drop enrichments that predate a server-side "welcome back" recap. The
  // server slices hidden pre-gap messages out of history, which shifts every
  // saved enrichment anchor — so clear them once per new recap. Keyed on the
  // stable resumed_at value so this is idempotent across reloads.
  function syncResumeState(j) {
    var ts = j && j.resumed_at != null ? String(j.resumed_at) : "";
    var prev = "";
    try { prev = localStorage.getItem(RESUME_KEY) || ""; } catch (e) {}
    if (ts !== prev) {
      if (ts) clearEnrichments();
      try { localStorage.setItem(RESUME_KEY, ts); } catch (e) {}
    }
  }

  // Render the full conversation from a GET /session payload (history +
  // takeover flag). Used on open, on session restore, and by the poll.
  function applySession(j) {
    syncResumeState(j);
    clearMessages();
    renderHistory(j.history);
    showGreetingIfEmpty();
    setTakeover(j.agent_takeover);
    renderedLen = (j.history || []).length;
    // Chips show only in the empty/greeting state — hide them when restoring a
    // conversation that already has messages.
    setChipsVisible(renderedLen === 0);
    lastSig = sessionSig(j);
  }

  // Append history entries we haven't shown yet, without clearing — so existing
  // product cards / order buttons / cart banners survive. Used by the poll while
  // a human agent (or the AI handing back) adds replies.
  function appendNewHistory(history) {
    history = history || [];
    if (history.length) setChipsVisible(false);
    holdScroll = true;
    for (var i = renderedLen; i < history.length; i++) {
      var m = history[i];
      if (m.role === "user" || m.role === "assistant") {
        addMessage(m.role, m.content, m.agent);
      }
    }
    holdScroll = false;
    renderedLen = history.length;
    scrollForNewContent();
  }

  function pollOnce() {
    if (!sessionId || turnInFlight) return Promise.resolve();
    return fetch(apiUrl + "/session/" + encodeURIComponent(sessionId), sessionGetOpts())
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        if (!j) return;
        syncResumeState(j);
        if (sessionSig(j) === lastSig) return;  // nothing changed server-side
        if (agentTakeover) {
          // A human agent — or the AI handing back — is adding replies: append
          // them (no clear), then reflect any hand-back to the bot.
          appendNewHistory(j.history);
          setTakeover(j.agent_takeover);
          lastSig = sessionSig(j);
        } else if (j.agent_takeover) {
          // Takeover just started: clean resync into takeover mode.
          applySession(j);
        } else {
          // Normal mode: the /chat response already rendered its own turn
          // (including product cards, which aren't in server history), and
          // finalizeTurn re-synced renderedLen from the server afterwards. So
          // any history BEYOND renderedLen here is a server-side addition we
          // haven't shown — most importantly the AI's catch-up reply after a
          // human hands the chat back. Append those instead of swallowing them;
          // if there's nothing new, just keep bookkeeping in step.
          var h = j.history || [];
          if (h.length > renderedLen) {
            appendNewHistory(h);
          } else {
            renderedLen = h.length;
          }
          lastSig = sessionSig(j);
        }
      })
      .catch(function () {});
  }

  // Poll a little faster while a human agent is on the line so their replies
  // land promptly; slower otherwise to stay well under the backend rate limit.
  function scheduleNextPoll() {
    if (!root.classList.contains("is-open")) return;
    var delay = agentTakeover ? 4000 : 8000;
    pollTimer = setTimeout(function () {
      pollOnce().then(scheduleNextPoll);
    }, delay);
  }

  function startPolling() {
    stopPolling();
    scheduleNextPoll();
  }

  function stopPolling() {
    if (pollTimer) { clearTimeout(pollTimer); pollTimer = null; }
  }

  // Reset the transcript to a brand-new, empty conversation.
  function showEmptySession() {
    clearMessages();
    showGreetingIfEmpty();
    setTakeover(false);
    setChipsVisible(true);  // empty conversation → chips available
    renderedLen = 0;
    lastSig = "";
  }

  // Resolves with true when it just synced state with the server (restored or
  // created a session), false when the session was already initialized — so
  // callers like openPanel() know whether a refresh fetch is still needed.
  function ensureSession() {
    if (initialized) return Promise.resolve(false);
    if (initializing) return initializing;

    initializing = (function () {
      var stored = null;
      try { stored = localStorage.getItem(SID_KEY); } catch (e) {}

      var restore = stored
        ? fetch(apiUrl + "/session/" + encodeURIComponent(stored), sessionGetOpts())
            .then(function (r) {
              if (!r.ok) {
                try { localStorage.removeItem(SID_KEY); } catch (e) {}
                clearEnrichments();  // stale session → its saved widgets are dead
                return null;
              }
              return r.json().then(function (j) {
                sessionId = stored;
                applySession(j);
                return true;
              });
            })
            .catch(function () { return null; })
        : Promise.resolve(null);

      return restore.then(function (ok) {
        if (ok) { initialized = true; return true; }
        var body = {};
        if (customerToken) {
          body.customer_token = customerToken;
          // Just logged in: ask the backend to carry the anonymous chat over.
          try {
            var prelogin = localStorage.getItem(PRELOGIN_KEY);
            if (prelogin) body.previous_session_id = prelogin;
          } catch (e) {}
        }
        return fetch(apiUrl + "/session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body)
        }).then(function (r) {
          if (!r.ok) throw new Error("Could not start chat session");
          return r.json();
        }).then(function (j) {
          var carried = !!body.previous_session_id &&
                        j.session_id === body.previous_session_id;
          sessionId = j.session_id;
          try {
            localStorage.setItem(SID_KEY, sessionId);
            localStorage.removeItem(PRELOGIN_KEY);  // used once, either way
          } catch (e) {}
          // Saved product cards belong to the carried-over chat only.
          if (!carried) clearEnrichments();
          pendingLoginContinue = carried && !!j.continue_after_login;
          // A logged-in customer's session can already hold a conversation
          // (the one carried over at login, or their saved one), so render it.
          if (customerToken) {
            return fetch(apiUrl + "/session/" + encodeURIComponent(sessionId), sessionGetOpts())
              .then(function (r) { return r.ok ? r.json() : null; })
              .catch(function () { return null; })
              .then(function (s) {
                if (s) applySession(s); else showEmptySession();
                initialized = true;
                return true;
              });
          }
          showEmptySession();
          initialized = true;
          return true;
        });
      });
    })();

    var attempt = initializing;
    attempt.catch(function (e) {
      addError("Could not start chat: " + e.message);
      // Allow a later retry, but never clobber a newer in-flight attempt.
      if (initializing === attempt) initializing = null;
    });

    return attempt;
  }

  // Run fn once the panel's open/close slide has finished, so heavy work —
  // the mobile page lock's full-page relayout, focusing the composer (which
  // raises the keyboard and resizes the panel) — lands after the animation
  // instead of in its frames. A newer call (or a close) supersedes a pending
  // one, so a quick open→close→open can't run a stale callback.
  var panelMotionToken = 0;
  var reduceMotionMQ = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)");
  function afterPanelTransition(fn) {
    var token = ++panelMotionToken;
    if (root.classList.contains("bici-chatbot--no-anim") || (reduceMotionMQ && reduceMotionMQ.matches)) {
      fn();
      return;
    }
    var done = false;
    function finish(e) {
      if (e && (e.target !== panel || e.propertyName !== "transform")) return;
      if (done) return;
      done = true;
      panel.removeEventListener("transitionend", finish);
      clearTimeout(timer);
      if (token === panelMotionToken) fn();
    }
    panel.addEventListener("transitionend", finish);
    // Fallback for a slide that never fires transitionend (already in place,
    // interrupted): a little longer than --bcb-open-dur.
    var timer = setTimeout(finish, 350);
  }

  function openPanel() {
    try { localStorage.setItem(OPEN_KEY, "true"); } catch (e) {}
    // Each open starts by following a trailing carousel again — whatever the
    // reader had scrolled to belonged to the last time the panel was up.
    followCarousel = true;
    root.classList.add("is-open");
    launcher.setAttribute("aria-expanded", "true");
    panel.setAttribute("aria-hidden", "false");
    panel.classList.remove("is-half");   // every open starts fullscreen
    syncPanelToViewport();
    // The closed panel is laid out (just hidden), but remeasure anyway in case
    // the chips changed while it was hidden.
    updateChipArrows();
    var slidIn = new Promise(function (resolve) {
      afterPanelTransition(function () { lockPage(); resolve(); });
    });
    ensureSession().then(function (justSynced) {
      // ensureSession() already fetched fresh state when it restored or created
      // the session; only reopens of an initialized session need a refresh (the
      // server may have agent replies added while the panel was closed).
      if (!justSynced && sessionId) {
        return fetch(apiUrl + "/session/" + encodeURIComponent(sessionId), sessionGetOpts())
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (j) { if (j) applySession(j); })
          .catch(function () {});
      }
    }).then(function () {
      // Just logged in from the bot's Log in button: pick the chat back up.
      if (pendingLoginContinue && sessionId) {
        pendingLoginContinue = false;
        continueAfterLogin();
      }
    }).finally(function () {
      startPolling();
      // Focus only once the panel has slid in: on mobile focusing raises the
      // keyboard, which resizes the panel mid-slide.
      slidIn.then(function () { inputEl && inputEl.focus(); });
    });
  }

  function closePanel() {
    try { localStorage.setItem(OPEN_KEY, "false"); } catch (e) {}
    panelMotionToken++;   // cancel a pending post-open lock/focus
    root.classList.remove("is-open");
    launcher.setAttribute("aria-expanded", "false");
    panel.setAttribute("aria-hidden", "true");
    unlockPage();
    syncPanelToViewport();
    stopPolling();
    launcher.focus();
  }

  function togglePanel() {
    if (root.classList.contains("is-open")) closePanel(); else openPanel();
  }

  launcher.addEventListener("click", togglePanel);
  closeBtn.addEventListener("click", closePanel);

  var mobileMQ = window.matchMedia("(max-width: 749px)");

  // Mobile keyboard handling. iOS doesn't shrink the layout viewport when the
  // on-screen keyboard opens — only the visual viewport — so a bottom-anchored
  // fullscreen panel keeps its composer (and send button) underneath the
  // keyboard. While the panel is open on mobile and a big chunk of the window
  // is obscured, pin the panel to the visual viewport instead; clear the
  // inline overrides as soon as the keyboard goes away so CSS rules apply.
  var visualVp = window.visualViewport || null;
  function syncPanelToViewport() {
    if (!visualVp) return;
    // While pinch-zoomed the visual viewport is a scaled sub-rectangle and
    // CSS-pixel math no longer maps onto the screen — don't pin, let the
    // fullscreen CSS rules stand until the zoom returns to 1.
    var zoomed = (visualVp.scale || 1) > 1.05;
    var obscured = window.innerHeight - visualVp.height;
    if (!zoomed && mobileMQ.matches && root.classList.contains("is-open") && obscured > 100) {
      panel.style.height = visualVp.height + "px";
      panel.style.top = visualVp.offsetTop + "px";
      panel.style.bottom = "auto";
      scrollForNewContent();
    } else {
      panel.style.height = "";
      panel.style.top = "";
      panel.style.bottom = "";
    }
  }
  if (visualVp) {
    visualVp.addEventListener("resize", syncPanelToViewport);
    visualVp.addEventListener("scroll", syncPanelToViewport);
  }

  // While the fullscreen panel is open on mobile, freeze the page behind it.
  // iOS Safari otherwise scrolls/bounces the page to "reveal" the focused
  // input when the keyboard opens, which shows the page moving around the
  // chat, and collapsing/expanding its toolbars mid-chat shifts the panel.
  // position:fixed on body is the technique iOS actually honors.
  var pageLockY = 0;
  var pageLocked = false;
  function lockPage() {
    if (pageLocked || !mobileMQ.matches) return;
    pageLockY = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = "fixed";
    document.body.style.top = -pageLockY + "px";
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    pageLocked = true;
  }
  function unlockPage() {
    if (!pageLocked) return;
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.width = "";
    window.scrollTo(0, pageLockY);
    pageLocked = false;
  }
  // Crossing the mobile/desktop breakpoint while open (rotation, window
  // resize): apply or release the lock to match the current layout.
  function onMobileMQChange() {
    if (!mobileMQ.matches) unlockPage();
    else if (root.classList.contains("is-open")) lockPage();
    syncPanelToViewport();
  }
  if (mobileMQ.addEventListener) mobileMQ.addEventListener("change", onMobileMQChange);
  else if (mobileMQ.addListener) mobileMQ.addListener(onMobileMQChange);

  // ── Mobile bottom-sheet gesture ────────────────────────────────────────
  // Dragging the header follows the finger by resizing the bottom-anchored
  // panel; on release it snaps to the nearest state — fullscreen, half-height
  // sheet, or (below roughly a third of the screen) closed.
  var SHEET_MIN_H = 140;   // don't let a live drag squash the panel below this
  var sheetDrag = null;    // { startY, startH } while a header drag is active

  function sheetViewportH() {
    return (visualVp && visualVp.height) || window.innerHeight;
  }

  headerEl.addEventListener("touchstart", function (e) {
    if (!mobileMQ.matches || e.touches.length !== 1) return;
    if (e.target && e.target.closest && e.target.closest(".bici-chatbot__close")) return;
    // Keyboard is up: a downward pull should dismiss it, not fight the
    // visual-viewport pinning for control of the panel's height.
    if (window.innerHeight - sheetViewportH() > 100) {
      if (inputEl) inputEl.blur();
      return;
    }
    endSheetSnap();   // a new drag follows the finger, not a leftover snap
    sheetDrag = { startY: e.touches[0].clientY, startH: panel.getBoundingClientRect().height };
    panel.classList.add("is-dragging");
  }, { passive: true });

  headerEl.addEventListener("touchmove", function (e) {
    if (!sheetDrag) return;
    e.preventDefault();
    var h = sheetDrag.startH - (e.touches[0].clientY - sheetDrag.startY);
    h = Math.max(SHEET_MIN_H, Math.min(sheetViewportH(), h));
    panel.style.height = h + "px";
    panel.style.top = "auto";
  }, { passive: false });

  function endSheetDrag(e) {
    if (!sheetDrag) return;
    var t = e.changedTouches && e.changedTouches[0];
    var h = sheetDrag.startH - ((t ? t.clientY : sheetDrag.startY) - sheetDrag.startY);
    var frac = h / sheetViewportH();
    sheetDrag = null;
    panel.classList.remove("is-dragging");
    if (frac < 0.32) {
      panel.classList.remove("is-half");
      closePanel();
      // closePanel() cleared the inline size; put the dragged height back so
      // the panel slides away from where the finger let go instead of jumping
      // to fullscreen first. Released once it's off screen (a reopen clears
      // it too, via syncPanelToViewport).
      panel.style.height = Math.max(SHEET_MIN_H, h) + "px";
      panel.style.top = "auto";
      afterPanelTransition(function () {
        panel.style.height = "";
        panel.style.top = "";
      });
      return;
    }
    // Animate from the dragged height to the snapped state (CSS only
    // transitions height while .is-snapping is set).
    panel.classList.add("is-snapping");
    clearTimeout(sheetSnapTimer);
    sheetSnapTimer = setTimeout(endSheetSnap, 300);
    panel.style.height = "";
    panel.style.top = "";
    panel.classList.toggle("is-half", frac < 0.72);
  }
  var sheetSnapTimer = null;
  function endSheetSnap() {
    clearTimeout(sheetSnapTimer);
    panel.classList.remove("is-snapping");
  }
  panel.addEventListener("transitionend", function (e) {
    if (e.target === panel && e.propertyName === "height") endSheetSnap();
  });
  headerEl.addEventListener("touchend", endSheetDrag);
  headerEl.addEventListener("touchcancel", endSheetDrag);

  // Clicks on links inside the panel:
  //  - Mobile: the panel is fullscreen, so following a link would leave the
  //    panel covering the destination. Close it — closePanel() clears OPEN_KEY
  //    so it won't reopen, and the shopper sees the page they navigated to.
  //  - Desktop: leave the panel open and let navigation proceed. OPEN_KEY is
  //    already "true" (set by openPanel), so the auto-reopen block restores the
  //    panel — with the transcript and cards — on the destination page.
  panel.addEventListener("click", function (e) {
    var t = e.target;
    var link = t && t.closest ? t.closest("a[href]") : null;
    if (!link || !panel.contains(link)) return;
    if (mobileMQ.matches) closePanel();
  });

  // Scroll chevrons for the chips row. The row's scrollbar is hidden, so with
  // a mouse there's no hint that more options sit off-screen; show a chevron
  // at whichever edge has hidden content (touch devices hide them in CSS and
  // pan the row directly).
  var coarsePointer = window.matchMedia &&
    window.matchMedia("(pointer: coarse)").matches;
  function updateChipArrows() {
    if (!chipsEl || !chipArrowL || !chipArrowR) return;
    var max = chipsEl.scrollWidth - chipsEl.clientWidth;
    chipArrowL.hidden = chipsEl.scrollLeft <= 2;
    chipArrowR.hidden = chipsEl.scrollLeft >= max - 2;
    // One chevron per edge at a time: while the row is expanded the close
    // toggle holds the left edge at the start of the row, and yields it to
    // the scroll-back arrow once the row is scrolled. Touch devices hide
    // the arrows in CSS, so the toggle always shows there.
    if (chipsToggle && chipsWrapEl &&
        !chipsWrapEl.classList.contains("bici-chatbot__chips-wrap--collapsed")) {
      chipsToggle.hidden = !chipArrowL.hidden && !coarsePointer;
    }
  }
  function scrollChips(dir) {
    // One click slides the row all the way to that end — the row is only a
    // few buttons long, so paging toward it in steps just wastes clicks.
    var target = dir > 0 ? chipsEl.scrollWidth - chipsEl.clientWidth : 0;
    var reduceMotion = window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (chipsEl.scrollTo) {
      chipsEl.scrollTo({ left: target, behavior: reduceMotion ? "auto" : "smooth" });
    } else {
      chipsEl.scrollLeft = target;
    }
  }
  if (chipsEl && chipArrowL && chipArrowR) {
    chipArrowL.addEventListener("click", function () { scrollChips(-1); });
    chipArrowR.addEventListener("click", function () { scrollChips(1); });
    chipsEl.addEventListener("scroll", updateChipArrows, { passive: true });
    window.addEventListener("resize", updateChipArrows);
  }

  // Collapse toggle: slide the chips row off to the right or back in.
  // Sending a message or using a chip also collapses it via
  // setChipsVisible(false).
  if (chipsToggle) {
    chipsToggle.addEventListener("click", function () {
      var collapsed = chipsWrapEl &&
        chipsWrapEl.classList.contains("bici-chatbot__chips-wrap--collapsed");
      setChipsVisible(!!collapsed);
    });
  }

  // Quick-action chips. "Track order" sends a prefilled message so the normal
  // /chat path runs orders_lookup (logged-in) or prompts to log in (anonymous).
  // "Report an issue" opens an inline form that POSTs to /report-issue on the
  // backend, which relays the report to the team's Slack channel (the webhook
  // URL is a secret and stays server-side).
  var trackChip = chipsEl && chipsEl.querySelector('[data-action="track"]');
  if (trackChip) {
    trackChip.addEventListener("click", function () {
      ensureSession().then(function () {
        if (sessionId) submit("Can you check the status of my order?");
      });
    });
  }

  // "Warranty claim" starts the warranty-intake flow via a prefilled message.
  var warrantyChip = chipsEl && chipsEl.querySelector('[data-action="warranty"]');
  if (warrantyChip) {
    warrantyChip.addEventListener("click", function () {
      ensureSession().then(function () {
        if (sessionId) submit("I'd like to start a warranty claim.");
      });
    });
  }

  var reportChip = chipsEl && chipsEl.querySelector('[data-action="report"]');
  var reportBox = null;

  function closeReportBox() {
    if (reportBox && reportBox.parentNode) reportBox.remove();
    reportBox = null;
    if (reportChip) reportChip.setAttribute("aria-expanded", "false");
  }

  function reportError(errEl, text, withFallbackLink) {
    errEl.textContent = text;
    if (withFallbackLink) {
      var fallback = safeUrl(config.reportFallbackUrl);
      if (fallback) {
        errEl.appendChild(document.createTextNode(" You can also reach us via "));
        var a = document.createElement("a");
        a.href = fallback;
        a.textContent = "our contact page";
        errEl.appendChild(a);
        errEl.appendChild(document.createTextNode("."));
      }
    }
    errEl.hidden = false;
  }

  function openReportBox() {
    if (reportBox) { closeReportBox(); return; }

    var box = el("div", "bici-chatbot__report");
    box.appendChild(el("div", "bici-chatbot__report-title", "Report an issue"));
    box.appendChild(el("div", "bici-chatbot__report-hint",
      "Tell us what went wrong and it goes straight to our team."));

    var textarea = document.createElement("textarea");
    textarea.className = "bici-chatbot__report-text";
    textarea.placeholder = "Describe the issue — what happened, and where?";
    textarea.rows = 4;
    textarea.maxLength = 2000;
    box.appendChild(textarea);

    // Logged-in shoppers are identified by their customer token; only ask
    // anonymous visitors for a way to reply.
    var emailInput = null;
    if (!customerLoggedIn) {
      emailInput = document.createElement("input");
      emailInput.type = "email";
      emailInput.className = "bici-chatbot__report-email";
      emailInput.placeholder = "Your email (optional, in case we need details)";
      emailInput.maxLength = 254;
      box.appendChild(emailInput);
    }

    var errEl = el("div", "bici-chatbot__report-error");
    errEl.hidden = true;
    box.appendChild(errEl);

    var actions = el("div", "bici-chatbot__report-actions");
    var cancelBtn = el("button", "bici-chatbot__report-cancel", "Cancel");
    cancelBtn.type = "button";
    cancelBtn.addEventListener("click", closeReportBox);
    var sendReportBtn = el("button", "bici-chatbot__report-send", "Send to our team");
    sendReportBtn.type = "button";
    actions.appendChild(cancelBtn);
    actions.appendChild(sendReportBtn);
    box.appendChild(actions);

    sendReportBtn.addEventListener("click", function () {
      var msg = textarea.value.trim();
      if (!msg) {
        reportError(errEl, "Please describe the issue first.", false);
        textarea.focus();
        return;
      }
      errEl.hidden = true;
      sendReportBtn.disabled = true;
      cancelBtn.disabled = true;
      sendReportBtn.textContent = "Sending…";

      var payload = { message: msg, page_url: window.location.href };
      if (sessionId) payload.session_id = sessionId;
      if (customerToken) payload.customer_token = customerToken;
      var email = emailInput ? emailInput.value.trim() : "";
      if (email) payload.email = email;

      fetch(apiUrl + "/report-issue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      }).then(function (r) {
        if (r.ok) {
          closeReportBox();
          var confirmation = "Thanks for flagging this — your report has gone " +
            "straight to our team, and a real person will take a look.";
          if (customerLoggedIn) {
            confirmation += " We'll follow up by email if we need more details.";
          } else if (email) {
            confirmation += " We'll follow up at " + email + " if we need more details.";
          }
          addMessage("assistant", confirmation);
          scrollToBottom();
          return;
        }
        // Failure: keep the box (and the visitor's text) so nothing is lost.
        if (r.status === 429) {
          reportError(errEl, "You've sent a few reports in a row — please wait a minute and try again.", false);
        } else {
          reportError(errEl, "Sorry — we couldn't send that just now. Please try again in a moment.", true);
        }
        return r.text().then(function (t) {
          console.error("[BiciChatbot] /report-issue failed:", r.status, t);
        });
      }).catch(function (e) {
        console.error("[BiciChatbot] /report-issue failed:", e);
        reportError(errEl, "Sorry — we couldn't send that just now. Please try again in a moment.", true);
      }).finally(function () {
        if (reportBox === box) {
          sendReportBtn.disabled = false;
          cancelBtn.disabled = false;
          sendReportBtn.textContent = "Send to our team";
        }
      });
    });

    reportBox = box;
    reportChip.setAttribute("aria-expanded", "true");
    messagesEl.appendChild(box);
    scrollToBottom();
    textarea.focus();
  }

  if (reportChip) {
    reportChip.addEventListener("click", openReportBox);
  }

  // Auto-reopen: the widget now renders site-wide, so if the panel was left
  // open (OPEN_KEY) — e.g. the shopper followed a link with it open — restore
  // it on this page. Suppress the rise animation this once so it doesn't flash
  // in.
  try {
    if (localStorage.getItem(OPEN_KEY) === "true") {
      root.classList.add("bici-chatbot--no-anim");
      openPanel();
      setTimeout(function () { root.classList.remove("bici-chatbot--no-anim"); }, 250);
    } else if (customerToken && localStorage.getItem(PRELOGIN_KEY)) {
      // Just logged in with a chat in progress (on mobile the Log in tap closed
      // the panel). Carry the chat over now, and if the bot was waiting on this
      // login, open the panel so its answer lands in view.
      ensureSession().then(function () {
        if (pendingLoginContinue) openPanel();
      }).catch(function () {});
    }
  } catch (e) {}

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && root.classList.contains("is-open")) closePanel();
  });

  // The composer is a textarea so long messages wrap and stay fully visible.
  // Grow it to fit what's typed: collapse to `auto` first so it shrinks back
  // when text is deleted, then take the content height. CSS min-height /
  // max-height clamp both ends, and past the cap overflow-y turns it into a
  // small scroller rather than letting it eat the transcript.
  function autoGrowInput() {
    inputEl.style.height = "auto";
    inputEl.style.height = inputEl.scrollHeight + "px";
    // The taller composer just took that height off the transcript; give a
    // trailing carousel's scroll bar its position back.
    keepCarouselBarVisible();
  }
  inputEl.addEventListener("input", autoGrowInput);

  // A textarea's Enter inserts a newline, so send has to be wired up by hand to
  // keep the single-line input's muscle memory. Shift+Enter (or Ctrl/Cmd+Enter)
  // is the newline. `isComposing` guards IME input, where Enter is picking a
  // candidate rather than finishing the message.
  inputEl.addEventListener("keydown", function (e) {
    if (e.key !== "Enter" || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.isComposing || e.keyCode === 229) return;
    e.preventDefault();
    if (sendBtn.disabled) return;   // a turn is already in flight
    formEl.requestSubmit ? formEl.requestSubmit() : formEl.dispatchEvent(new Event("submit", { cancelable: true }));
  });

  formEl.addEventListener("submit", function (e) {
    e.preventDefault();
    var msg = inputEl.value.trim();
    if (!msg) return;
    if (!sessionId) {
      ensureSession().then(function () { if (sessionId) submit(msg); });
      return;
    }
    submit(msg);
  });

  function submit(msg) {
    inputEl.value = "";
    autoGrowInput();   // a multi-line message left the composer tall; collapse it
    setChipsVisible(false);  // conversation has started
    // In takeover the visitor's message comes back via the poll (from server
    // history); rendering it here too would double it. Show it immediately only
    // in normal mode.
    var mine = agentTakeover ? null : addMessage("user", msg);
    runTurn({
      session_id: sessionId,
      message: msg,
      page_url: window.location.href
    }, mine);
  }

  // The bot asked this customer to log in and they just did: run its next turn
  // with no typed message (the backend answers the request that was waiting).
  function continueAfterLogin() {
    if (agentTakeover) return;
    runTurn({
      session_id: sessionId,
      event: "logged_in",
      page_url: window.location.href
    });
  }

  // The server is still finishing an earlier reply for this chat (HTTP 409),
  // typically one started on the previous page. Wait for it, show it, then
  // send. A turn never runs anywhere near this long, so giving up means stuck.
  var BUSY = {};
  var BUSY_RETRY_MS = 2500;
  var BUSY_MAX_TRIES = 24;

  // Show replies the server saved that aren't on screen yet (the earlier turn
  // that held us up), above this turn's own message and "Thinking…" bubble.
  function catchUpBefore(nodes) {
    return fetch(apiUrl + "/session/" + encodeURIComponent(sessionId), sessionGetOpts())
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        if (!j || (j.history || []).length <= renderedLen) return;
        nodes = nodes.filter(function (n) { return n && n.parentNode; });
        nodes.forEach(function (n) { n.remove(); });
        appendNewHistory(j.history);
        nodes.forEach(function (n) { messagesEl.appendChild(n); });
        lastSig = sessionSig(j);
        scrollForNewContent();
      })
      .catch(function () {});
  }

  function runTurn(chatBody, ownMessage) {
    sendBtn.disabled = true;
    turnInFlight = true;

    // No "Thinking…" while a human has taken over — there's no LLM turn; their
    // reply arrives on the next poll, not in this response.
    var thinking = null;
    if (!agentTakeover) {
      thinking = el("div", "bici-chatbot__msg bici-chatbot__msg--assistant bici-chatbot__msg--typing", "Thinking…");
      messagesEl.appendChild(thinking);
      scrollToBottom();
    }

    if (customerToken) chatBody.customer_token = customerToken;

    var tries = 0;
    function attempt() {
      var useStream = canStream && streamEndpointOk && !agentTakeover;
      var turn = useStream ? streamTurn(chatBody, thinking) : bufferedTurn(chatBody, thinking);
      return turn.then(function (res) {
        if (res !== BUSY) return;
        if (++tries >= BUSY_MAX_TRIES) {
          if (thinking) thinking.remove();
          addError("Still finishing the last reply. Please try again in a moment.");
          return;
        }
        return new Promise(function (resolve) { setTimeout(resolve, BUSY_RETRY_MS); })
          .then(function () { return catchUpBefore([ownMessage, thinking]); })
          .then(attempt);
      });
    }
    attempt().catch(function (e) {
      if (thinking && thinking.parentNode) thinking.remove();
      addError("Network error: " + e.message);
    }).finally(function () {
      sendBtn.disabled = false;
      turnInFlight = false;
      // The server history grew this turn, so its signature differs from lastSig
      // and the next poll reconciles bookkeeping (renderedLen) without
      // re-rendering — no forced wipe needed.
      inputEl.focus();
    });
  }

  // Render a turn's final payload: reply text, rich widgets, scroll parking and
  // enrichment persistence. `existingBubble` comes from the streaming path
  // (text already on screen); the buffered path passes null.
  function finalizeTurn(j, existingBubble) {
    // A human support agent is handling this chat: the backend recorded the
    // message but didn't generate a reply. Show the takeover state; the
    // agent's response will arrive via the background poll.
    if (j.agent_takeover) {
      setTakeover(true);
      return;
    }
    // Render the whole turn without auto-scrolling, then scroll once: down to
    // the bottom, or until the visitor's message just leaves the viewport —
    // whichever comes first — so the reader isn't thrown past unread content.
    holdScroll = true;
    if (j.text) {
      if (existingBubble) {
        // The final text is authoritative; normally identical to what streamed.
        existingBubble.textContent = j.text;
      } else {
        addMessage("assistant", j.text);
      }
    }
    if (j.products && j.products.length) renderProductCards(j.products);
    if (j.orders && j.orders.length) renderOrderButtons(j.orders);
    if (j.login_url) renderLoginButton(j.login_url);
    if (j.cart) renderCartBanner(j.cart);
    if (j.shipping) renderShippingTable(j.shipping);
    holdScroll = false;
    scrollForNewContent();

    // Resync bookkeeping against the authoritative server history after EVERY
    // turn (not just rich ones): a turn can add 1–3 text messages depending on
    // tool use, so rather than count locally we read the real length. Keeping
    // renderedLen accurate on plain-text turns too is what lets the normal-mode
    // poll safely append server-side additions (the AI's catch-up reply after a
    // human hands the chat back) without double-rendering this turn. Returning
    // the promise keeps turnInFlight true until it finishes, so no poll
    // interleaves. Rich widgets aren't in server history, so persist them here.
    var hasRich = (j.products && j.products.length) ||
                  (j.orders && j.orders.length) || j.cart || j.shipping ||
                  j.login_url;
    if (sessionId) {
      var rich = hasRich
        ? { products: j.products || [], orders: j.orders || [], cart: j.cart || null,
            shipping: j.shipping || null, login_url: j.login_url || null }
        : null;
      return fetch(apiUrl + "/session/" + encodeURIComponent(sessionId), sessionGetOpts())
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (s) {
          if (!s) return;
          var len = (s.history || []).length;
          if (rich) saveEnrichment(len, rich);
          renderedLen = len;
          lastSig = sessionSig(s);
        })
        .catch(function () {});
    }
  }

  // Classic buffered turn: one POST /chat, render everything when it lands.
  // Also the fallback when the backend has no /chat/stream.
  function bufferedTurn(chatBody, thinking) {
    return fetch(apiUrl + "/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(chatBody)
    }).then(function (r) {
      if (r.status === 409) return BUSY;
      if (thinking) thinking.remove();
      if (r.status === 429) {
        addError("Rate limit hit. Please slow down.");
        return null;
      }
      if (!r.ok) {
        // Never surface raw backend error bodies to shoppers; keep the detail
        // in the console for debugging.
        return r.text().then(function (t) {
          console.error("[BiciChatbot] /chat failed:", r.status, t);
          addError("Something went wrong — please try again.");
          return null;
        });
      }
      return r.json();
    }).then(function (j) {
      if (!j || j === BUSY) return j;
      return finalizeTurn(j, null);
    });
  }

  // Streaming turn: POST /chat/stream (SSE) and render text as the model
  // writes, with per-tool status in the typing bubble. Falls back to
  // bufferedTurn if the endpoint doesn't exist.
  function streamTurn(chatBody, thinking) {
    return fetch(apiUrl + "/chat/stream", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(chatBody)
    }).then(function (r) {
      if (r.status === 404 || r.status === 405) {
        // A 404 is ambiguous: the endpoint also 404s for an expired session.
        // Only a route-level miss (no "session" in the detail) means the
        // backend lacks streaming — then remember and use the buffered path.
        return r.text().then(function (t) {
          if (/session/i.test(t)) {
            if (thinking) thinking.remove();
            console.error("[BiciChatbot] /chat/stream failed:", r.status, t);
            addError("Something went wrong — please try again.");
            return null;
          }
          streamEndpointOk = false;
          return bufferedTurn(chatBody, thinking);
        });
      }
      if (r.status === 409) return BUSY;
      if (r.status === 429) {
        if (thinking) thinking.remove();
        addError("Rate limit hit. Please slow down.");
        return null;
      }
      if (!r.ok) {
        return r.text().then(function (t) {
          if (thinking) thinking.remove();
          console.error("[BiciChatbot] /chat/stream failed:", r.status, t);
          addError("Something went wrong — please try again.");
          return null;
        });
      }
      return consumeStream(r, thinking);
    });
  }

  function consumeStream(r, thinking) {
    var decoder = new TextDecoder();
    var buffer = "";
    var currentBubble = null;   // assistant bubble receiving deltas
    var lastBubble = null;      // most recent streamed bubble, even once closed
    var typing = thinking;      // "Thinking…" / tool-status bubble
    var finalPayload = null;

    function setTyping(label) {
      if (typing) {
        typing.textContent = label;
      } else {
        typing = el("div", "bici-chatbot__msg bici-chatbot__msg--assistant bici-chatbot__msg--typing", label);
        messagesEl.appendChild(typing);
        scrollForNewContent();
      }
    }
    function clearTyping() {
      if (typing) { typing.remove(); typing = null; }
    }

    function handleEvent(ev) {
      if (ev.type === "delta") {
        clearTyping();
        if (!currentBubble) currentBubble = lastBubble = addMessage("assistant", "");
        currentBubble.textContent += ev.text;
        // Follow the growing text down until the visitor's message would
        // scroll out of view, then hold there so long replies don't fly past.
        scrollForNewContent();
      } else if (ev.type === "status") {
        // A tool is running; any text after it is a NEW assistant message in
        // server history, so close the current bubble to match.
        currentBubble = null;
        setTyping(TOOL_STATUS_LABELS[ev.tool] || "Working on it…");
      } else if (ev.type === "final") {
        finalPayload = ev;
      } else if (ev.type === "error") {
        clearTyping();
        console.error("[BiciChatbot] stream error:", ev.error);
        addError("Something went wrong — please try again.");
      }
    }

    function processBuffer(flushAll) {
      var parts = buffer.split("\n\n");
      buffer = flushAll ? "" : parts.pop();
      parts.forEach(function (chunk) {
        chunk.split("\n").forEach(function (line) {
          if (line.indexOf("data: ") === 0) {
            try { handleEvent(JSON.parse(line.slice(6))); } catch (e) {}
          }
        });
      });
    }

    function finish() {
      clearTyping();
      if (!finalPayload) return;
      // The reply can be text that streamed before a tool ran (e.g. the login
      // ask beside the order lookup); reuse that bubble rather than repeat it.
      var bubble = currentBubble;
      if (!bubble && lastBubble && finalPayload.text &&
          lastBubble.textContent.trim() === finalPayload.text.trim()) {
        bubble = lastBubble;
      }
      return finalizeTurn(finalPayload, bubble);
    }

    var read;
    if (r.body && r.body.getReader) {
      var reader = r.body.getReader();
      read = function pump() {
        return reader.read().then(function (step) {
          if (step.done) { processBuffer(true); return finish(); }
          buffer += decoder.decode(step.value, { stream: true });
          processBuffer(false);
          return pump();
        });
      };
    } else {
      // No ReadableStream on the response (or a proxy buffered it): parse the
      // whole SSE body at once — same rendering, just not incremental.
      read = function () {
        return r.text().then(function (t) {
          buffer = t;
          processBuffer(true);
          return finish();
        });
      };
    }
    return read().catch(function (e) {
      clearTyping();
      throw e;
    });
  }

  window.BiciChatbot = {
    open: openPanel,
    close: closePanel,
    toggle: togglePanel
  };
})();